/**
 * MfaSettingsSection
 *
 * Security settings section displayed in the guide account settings page.
 * Shows current MFA status and provides controls to enable/disable MFA.
 *
 * - Calls GET /api/v1/auth/mfa/status on mount
 * - "Enable Authenticator App" opens MfaSetupModal (visible when MFA disabled)
 * - "Disable MFA" with password confirmation (visible when MFA enabled)
 *
 * Epic: MVP 1.15 (V2) — Federated Auth, Epic 4: MFA
 * Story: SCRUM-356 — Guide MFA Setup
 */

import React, { useState, useEffect } from 'react';
import { Shield, Loader, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { mfaApi } from '../../services/mfaApi';
import MfaStatusBadge from './MfaStatusBadge';
import MfaSetupModal from './MfaSetupModal';

export default function MfaSettingsSection() {
  // MFA status from API
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);

  // Setup modal state
  const [setupOpen, setSetupOpen] = useState(false);

  // Disable flow state
  const [disableMode, setDisableMode] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const response = await mfaApi.getStatus();
      const data = response?.data || {};
      setMfaEnabled(!!data.mfaEnabled);
      setMfaMethod(data.mfaMethod || null);
    } catch (err) {
      setStatusError(err?.message || 'Failed to load MFA status.');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSetupSuccess = () => {
    setMfaEnabled(true);
    setMfaMethod('TOTP');
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!disablePassword) {
      setDisableError('Password is required to disable MFA.');
      return;
    }
    setDisableLoading(true);
    setDisableError(null);
    try {
      await mfaApi.disable(disablePassword);
      setMfaEnabled(false);
      setMfaMethod(null);
      setDisableMode(false);
      setDisablePassword('');
    } catch (err) {
      setDisableError(err?.message || 'Failed to disable MFA. Please check your password and try again.');
    } finally {
      setDisableLoading(false);
    }
  };

  const cancelDisable = () => {
    setDisableMode(false);
    setDisablePassword('');
    setDisableError(null);
  };

  if (statusLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader size={16} className="animate-spin" />
        Loading MFA status…
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
        <AlertCircle size={16} className="mt-0.5 shrink-0" />
        <span>{statusError}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <Shield size={18} className="text-gray-700" />
        <h3 className="text-base font-semibold text-gray-900">Two-Factor Authentication</h3>
      </div>

      <p className="text-sm text-gray-600">
        Add an extra layer of security to your account. When enabled, you&apos;ll need to enter a
        verification code from your authenticator app when signing in.
      </p>

      {/* Current status */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-700 font-medium">Status:</span>
        <MfaStatusBadge mfaEnabled={mfaEnabled} mfaMethod={mfaMethod} />
      </div>

      {/* Controls */}
      {!mfaEnabled && !disableMode && (
        <button
          type="button"
          onClick={() => setSetupOpen(true)}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90 transition-colors"
        >
          Enable Authenticator App
        </button>
      )}

      {mfaEnabled && !disableMode && (
        <button
          type="button"
          onClick={() => setDisableMode(true)}
          className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
        >
          Disable MFA
        </button>
      )}

      {/* Disable confirmation form */}
      {disableMode && (
        <form onSubmit={handleDisable} className="space-y-3 p-4 border border-red-200 rounded-lg bg-red-50">
          <p className="text-sm text-red-700 font-medium">
            Enter your password to confirm disabling MFA.
          </p>

          {disableError && (
            <div className="flex items-start gap-2 text-sm text-red-600">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{disableError}</span>
            </div>
          )}

          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Your current password"
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
              autoComplete="current-password"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelDisable}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={disableLoading || !disablePassword}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            >
              {disableLoading ? <Loader size={14} className="animate-spin" /> : null}
              Disable MFA
            </button>
          </div>
        </form>
      )}

      {/* Setup modal */}
      <MfaSetupModal
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onSuccess={handleSetupSuccess}
      />
    </div>
  );
}
